import { useState } from "react";

import { useNavigate } from "@tanstack/react-router";
import { Check, FileText, Shield, Upload } from "lucide-react";
import { useDispatch } from "react-redux";

import {
  ASSESSMENT_TYPES,
  COMMON_SPECIALTIES,
  OTHER_SPECIALTIES,
} from "@constants/assessment-types";
import { NAVIGATION_PATHS } from "@constants/navigation";

import { useCreateReferrerCase } from "@hooks/mutations/use-provider-requests";

import { notifyError } from "@utils/notification";
import { cn } from "@utils/ui";

const STEPS = ["Service type", "Specialty", "Claimant", "Case details"] as const;

// ── design-matched primitives ────────────────────────────────────────────────
const Field = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <label className="block">
    <span className="mb-1.5 block text-xs font-semibold text-[#5A5A6B]">
      {label}
    </span>
    {children}
  </label>
);

const inputCls =
  "w-full rounded-[10px] border border-[#E2E5EB] px-[13px] py-2.5 text-sm text-[#1A1939] outline-none focus:border-[#0065CB]";

const TextInput = (props: React.InputHTMLAttributes<HTMLInputElement>) => (
  <input {...props} className={inputCls} />
);

const SectionBar = ({ children }: { children: React.ReactNode }) => (
  <div className="rounded-[9px] bg-[#e8f2fb] px-4 py-[11px] text-[13.5px] font-semibold text-[#0065CB]">
    {children}
  </div>
);

const OptionButton = ({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "rounded-[10px] border-[1.5px] px-[14px] py-3 text-left text-[13px] font-semibold transition-colors",
      active
        ? "border-[#0065CB] bg-[#f0f7ff] text-[#0065CB]"
        : "border-[#E2E5EB] bg-white text-[#1A1939] hover:border-[#0065CB]/40"
    )}
  >
    {label}
  </button>
);

const YesNo = ({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean | null;
  onChange: (v: boolean) => void;
}) => (
  <div className="flex items-center gap-[18px]">
    <span className="w-[200px] text-[13.5px] font-semibold text-[#1A1939]">
      {label}
    </span>
    {[true, false].map((v) => (
      <button
        key={String(v)}
        type="button"
        onClick={() => onChange(v)}
        className={cn(
          "rounded-[9px] border-[1.5px] px-[18px] py-[7px] text-[13px] font-semibold transition-colors",
          value === v
            ? "border-[#0065CB] bg-[#f0f7ff] text-[#0065CB]"
            : "border-[#E2E5EB] bg-white text-[#1A1939]"
        )}
      >
        {v ? "Yes" : "No"}
      </button>
    ))}
  </div>
);

const cardCls =
  "rounded-[14px] border border-[#E2E5EB] bg-white p-6 shadow-[0_2px_8px_rgba(26,25,57,0.05)]";

/**
 * New case — the referrer portal's full-page create wizard (MEDirect 2.0 design).
 * 4 steps: Service type → Specialty → Claimant → Case details. On "Create case"
 * it creates a lightweight intake case and lands on the Cases → Case summary.
 * (Reached from the sidebar "New case" item and the Cases list header button.)
 */
const NewCasePage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const createCase = useCreateReferrerCase();

  const [step, setStep] = useState(1);

  const [serviceType, setServiceType] = useState("");
  const [specialty, setSpecialty] = useState("");
  const [showOtherSpecialty, setShowOtherSpecialty] = useState(false);

  const [secureOn, setSecureOn] = useState(false);
  const [title, setTitle] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [dob, setDob] = useState("");
  const [employerName, setEmployerName] = useState("");

  const [claimNumber, setClaimNumber] = useState("");
  const [referrerEmail, setReferrerEmail] = useState("");
  const [dateOfInjury, setDateOfInjury] = useState("");
  const [interpreterRequired, setInterpreterRequired] = useState<boolean | null>(
    null
  );
  const [urgentReport, setUrgentReport] = useState<boolean | null>(null);
  const [reportAddressed, setReportAddressed] = useState<boolean | null>(null);
  const [comments, setComments] = useState("");
  const [docsComplete, setDocsComplete] = useState<boolean | null>(null);

  const canNext =
    (step === 1 && !!serviceType) ||
    (step === 2 && !!specialty) ||
    (step === 3 &&
      (secureOn || (firstName.trim() !== "" && lastName.trim() !== ""))) ||
    step === 4;

  const canCreate = claimNumber.trim() !== "" && !createCase.isPending;

  const handleCreate = async () => {
    if (!canCreate) return;
    try {
      const c = await createCase.mutateAsync({
        examinee_name: `${firstName.trim()} ${lastName.trim()}`.trim(),
        claim_number: claimNumber.trim(),
        // Admin/staff create on a referrer's behalf; this assigns the case to
        // that referrer (MDRS-157). Blank → assigned to the creator.
        referrer_email: referrerEmail.trim() || null,
        service_type: serviceType || null,
        specialty: specialty || null,
        claimant_mobile: mobile.trim() || null,
        claimant_email: email.trim() || null,
        date_of_birth: dob || null,
        employer_name: employerName.trim() || null,
        date_of_injury: dateOfInjury || null,
        interpreter_required: interpreterRequired,
        urgent_report: urgentReport,
        report_addressed_to_referrer: reportAddressed,
        comments: comments.trim() || null,
      });
      if (c?.case_number) {
        navigate({ to: `${NAVIGATION_PATHS.CASES}/${c.case_number}` });
      }
    } catch (err) {
      notifyError(err, dispatch, "Failed to create case");
    }
  };

  return (
    <div className="min-h-full bg-[#F5F7FA] px-10 py-8">
      <div className="mx-auto max-w-[1040px]">
        <h1 className="text-[26px] font-bold tracking-[-0.01em] text-[#1A1939]">
          New case
        </h1>
        <p className="text-sm text-[#5A5A6B]">
          Capture the case details — you can request documents and book an
          appointment from the case afterwards.
        </p>

        {/* Progress stepper */}
        <div className="my-6 flex items-center">
          {STEPS.map((label, i) => {
            const n = i + 1;
            const done = n < step;
            const active = n === step;
            return (
              <div key={label} className="flex min-w-0 flex-1 items-center">
                <div className="flex flex-none items-center gap-[9px]">
                  <div
                    className={cn(
                      "flex h-7 w-7 flex-none items-center justify-center rounded-full border-[1.5px] text-xs font-bold",
                      active
                        ? "border-[#0065CB] bg-[#0065CB] text-white"
                        : done
                          ? "border-[#0065CB] bg-[#e8f2fb] text-[#0065CB]"
                          : "border-[#E2E5EB] bg-white text-[#8A8A98]"
                    )}
                  >
                    {done ? <Check className="h-3 w-3" /> : `0${n}`}
                  </div>
                  <span
                    className={cn(
                      "whitespace-nowrap text-xs font-semibold",
                      active || done ? "text-[#1A1939]" : "text-[#8A8A98]"
                    )}
                  >
                    {label}
                  </span>
                </div>
                {n < STEPS.length && (
                  <div
                    className={cn(
                      "mx-[10px] h-0.5 flex-1",
                      done ? "bg-[#0065CB]" : "bg-[#E2E5EB]"
                    )}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Step 1 — Service type */}
        {step === 1 && (
          <div className={cardCls}>
            <div className="mb-1.5 text-[15px] font-bold text-[#1A1939]">
              Assessment type
            </div>
            <div className="mb-5 text-[13px] text-[#5A5A6B]">
              Choose the assessment type required for this matter.
            </div>
            <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2">
              {ASSESSMENT_TYPES.map((t) => (
                <OptionButton
                  key={t}
                  label={t}
                  active={serviceType === t}
                  onClick={() => setServiceType(t)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Step 2 — Specialty */}
        {step === 2 && (
          <div className={cardCls}>
            <div className="mb-1.5 text-[15px] font-bold text-[#1A1939]">
              Specialty required
            </div>
            <div className="mb-5 text-[13px] text-[#5A5A6B]">
              Select the type of expert you need. Choose Other to search the full
              list.
            </div>
            <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2">
              {COMMON_SPECIALTIES.map((s) => (
                <OptionButton
                  key={s}
                  label={s}
                  active={specialty === s && !showOtherSpecialty}
                  onClick={() => {
                    setSpecialty(s);
                    setShowOtherSpecialty(false);
                  }}
                />
              ))}
              <OptionButton
                label="Other specialty"
                active={showOtherSpecialty}
                onClick={() => {
                  setShowOtherSpecialty(true);
                  setSpecialty("");
                }}
              />
            </div>
            {showOtherSpecialty && (
              <div className="mt-4">
                <Field label="Search specialties">
                  <select
                    className={inputCls}
                    value={specialty}
                    onChange={(e) => setSpecialty(e.target.value)}
                  >
                    <option value="">Select a specialty</option>
                    {OTHER_SPECIALTIES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
            )}
          </div>
        )}

        {/* Step 3 — Claimant */}
        {step === 3 && (
          <div className={cardCls}>
            <div className="mb-[18px] flex items-center justify-between gap-3.5">
              <div className="text-base font-bold text-[#1A1939]">
                Claimant Details
              </div>
              <button
                type="button"
                onClick={() => setSecureOn((s) => !s)}
                className="rounded-[9px] bg-[#1FA37A] px-4 py-[9px] text-[13px] font-semibold text-white"
              >
                {secureOn ? "Secure & Switch · on" : "Secure & Switch"}
              </button>
            </div>
            <SectionBar>Claim / injured person details</SectionBar>
            {secureOn && (
              <div className="mt-[18px] flex items-start gap-2.5 rounded-[10px] border border-[#f0e0bd] bg-[#fdf7ec] px-4 py-[13px] text-[13px] text-[#8a6d2b]">
                <Shield className="h-[17px] w-[17px] flex-none text-[#b8860b]" />
                Secure &amp; Switch is on. The appointment is held and claimant
                details can be added later.
              </div>
            )}
            <div className="mt-[18px] grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field label="Title">
                <TextInput
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Mr / Ms / Mrs"
                />
              </Field>
              <div className="hidden md:block" />
              <Field label="First name">
                <TextInput
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </Field>
              <Field label="Last name">
                <TextInput
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </Field>
              <Field label="Mobile">
                <TextInput
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value)}
                  placeholder="04xx xxx xxx"
                />
              </Field>
              <Field label="Email address">
                <TextInput
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="claimant@email.com.au"
                />
              </Field>
              <Field label="Date of birth">
                <TextInput
                  type="date"
                  value={dob}
                  onChange={(e) => setDob(e.target.value)}
                />
              </Field>
            </div>
            <div className="mb-4 mt-[22px]">
              <SectionBar>Claimant&apos;s employer details</SectionBar>
            </div>
            <div className="md:max-w-[50%]">
              <Field label="Claimant's employer name">
                <TextInput
                  value={employerName}
                  onChange={(e) => setEmployerName(e.target.value)}
                />
              </Field>
            </div>
          </div>
        )}

        {/* Step 4 — Case details */}
        {step === 4 && (
          <div className={cardCls}>
            <div className="mb-4 text-base font-bold text-[#1A1939]">
              Case details
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field label="Claim no. / Ref. no.">
                <TextInput
                  value={claimNumber}
                  onChange={(e) => setClaimNumber(e.target.value)}
                  placeholder="e.g. WC-2026-12345"
                />
              </Field>
              <Field label="Date of injury">
                <TextInput
                  type="date"
                  value={dateOfInjury}
                  onChange={(e) => setDateOfInjury(e.target.value)}
                />
              </Field>
              <Field label="Referrer email (assign this case to a referrer)">
                <TextInput
                  type="email"
                  value={referrerEmail}
                  onChange={(e) => setReferrerEmail(e.target.value)}
                  placeholder="referrer@example.com — leave blank to keep it yours"
                />
              </Field>
            </div>
            <div className="mt-[18px] flex flex-col gap-3.5">
              <YesNo
                label="Interpreter required?"
                value={interpreterRequired}
                onChange={setInterpreterRequired}
              />
              <YesNo
                label="Urgent report?"
                value={urgentReport}
                onChange={setUrgentReport}
              />
              <YesNo
                label="Is the report addressed to you?"
                value={reportAddressed}
                onChange={setReportAddressed}
              />
            </div>
            <div className="mt-[18px]">
              <Field label="Comments in relation to this booking, if required">
                <textarea
                  className={cn(inputCls, "min-h-[80px] resize-y")}
                  rows={3}
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                />
              </Field>
            </div>

            <div className="mb-4 mt-[22px]">
              <SectionBar>Supporting documents</SectionBar>
            </div>
            <div className="mb-3.5 cursor-pointer rounded-[10px] border-[1.5px] border-dashed border-[#cdddfb] bg-[#f0f7ff] p-[18px] text-center">
              <Upload className="mx-auto mb-1.5 h-[22px] w-[22px] text-[#0065CB]" />
              <div className="text-[13.5px] font-semibold text-[#0065CB]">
                Upload medical records, referral letter, imaging and reports
              </div>
              <div className="mt-[3px] text-xs text-[#8A8A98]">
                PDF, DOCX, JPG · you can also add these from the case after creating
              </div>
            </div>
            <div className="mb-2.5 text-[13.5px] font-semibold text-[#1A1939]">
              Are all documents for this case uploaded?
            </div>
            <div className="flex flex-wrap gap-2.5">
              <button
                type="button"
                onClick={() => setDocsComplete(true)}
                className={cn(
                  "flex items-center gap-2 rounded-[10px] border-[1.5px] px-4 py-2.5 text-[13px] font-semibold",
                  docsComplete === true
                    ? "border-[#0065CB] bg-[#f0f7ff] text-[#0065CB]"
                    : "border-[#E2E5EB] bg-white text-[#1A1939]"
                )}
              >
                <Check className="h-[15px] w-[15px]" /> All documents uploaded
              </button>
              <button
                type="button"
                onClick={() => setDocsComplete(false)}
                className={cn(
                  "rounded-[10px] border-[1.5px] px-4 py-2.5 text-[13px] font-semibold",
                  docsComplete === false
                    ? "border-[#E08A00] bg-[#fdf7ec] text-[#8a6d22]"
                    : "border-[#E2E5EB] bg-white text-[#1A1939]"
                )}
              >
                Some documents still to come
              </button>
            </div>

            <div className="my-[18px] flex items-center gap-2.5">
              <div className="h-px flex-1 bg-[#eef1f6]" />
              <span className="text-[11.5px] font-semibold text-[#8A8A98]">
                or request them from a provider
              </span>
              <div className="h-px flex-1 bg-[#eef1f6]" />
            </div>
            <div className="rounded-[10px] border border-[#E2E5EB] p-4">
              <div className="flex items-start gap-2.5">
                <FileText className="mt-px h-[18px] w-[18px] flex-none text-[#0065CB]" />
                <div>
                  <div className="text-[13.5px] font-semibold text-[#1A1939]">
                    Request documents from a provider
                  </div>
                  <div className="mt-0.5 text-xs text-[#8A8A98]">
                    Create the case first, then request records from a provider
                    (SureMed) on the case&apos;s Documents section — they&apos;re
                    attached automatically as they arrive.
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Footer nav */}
        <div className="mt-6 flex items-center justify-between">
          {step > 1 ? (
            <button
              type="button"
              onClick={() => setStep((s) => s - 1)}
              className="rounded-[10px] border border-[#E2E5EB] bg-white px-5 py-2.5 text-sm font-semibold text-[#1A1939]"
            >
              Prev
            </button>
          ) : (
            <span />
          )}
          {step < 4 ? (
            <button
              type="button"
              disabled={!canNext}
              onClick={() => setStep((s) => s + 1)}
              className="rounded-[10px] bg-[#0065CB] px-6 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
            >
              Next
            </button>
          ) : (
            <button
              type="button"
              disabled={!canCreate}
              onClick={handleCreate}
              className="rounded-[10px] bg-[#0065CB] px-6 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
            >
              {createCase.isPending ? "Creating…" : "Create case"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewCasePage;
